import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import SetupPasswordPage from "./pages/SetupPasswordPage";
import AdminLayout from "./components/AdminLayout";
import DashboardPage from "./pages/admin/DashboardPage";
import ImmobiliPage from "./pages/admin/ImmobiliPage";
import UtentiPage from "./pages/admin/UtentiPage";
import ContrattiPage from "./pages/admin/ContrattiPage";
import DocumentiPage from "./pages/admin/DocumentiPage";
import PagamentiPage from "./pages/admin/PagamentiPage";
import PagamentiStraordinariPage from "./pages/admin/PagamentiStraordinariPage";
import SollecitiPage from "./pages/admin/SollecitiPage";
import AuditLogsPage from "./pages/admin/AuditLogsPage";
import AvvisiPage from "./pages/admin/AvvisiPage";
import TemplateTestiPage from "./pages/admin/TemplateTestiPage";
import TemplateDocumentiPage from "./pages/admin/TemplateDocumentiPage";
import UserDashboard from "./pages/user/UserDashboard";
import GhostDashboard from "./pages/admin/GhostDashboard";
import StoragePage from "./pages/admin/StoragePage";
import EmailPage from "./pages/admin/EmailPage";
import RegistroGdprPage from "./pages/admin/RegistroGdprPage";
import SecurityCenterPage from "./pages/admin/SecurityCenterPage";
import PortaleUtentePreview from "./pages/PortaleUtentePreview";
import PrivacyPage from "./pages/PrivacyPage";
import FaqPage from "./pages/FaqPage";
import CookieBanner from "./components/CookieBanner";

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && user.ruolo !== 'admin') {
    return <Navigate to="/user" replace />;
  }

  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (user) {
    return <Navigate to={user.ruolo === 'admin' ? '/admin' : '/user'} replace />;
  }

  return children;
};

// Root Redirect
const RootRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to={user.ruolo === 'admin' ? '/admin' : '/user'} replace />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Root redirect */}
      <Route path="/" element={<RootRedirect />} />

      {/* Public routes */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } 
      />

      {/* Password Setup (public) */}
      <Route path="/setup-password" element={<SetupPasswordPage />} />

      {/* Admin routes */}
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute requireAdmin>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="immobili" element={<ImmobiliPage />} />
        <Route path="utenti" element={<UtentiPage />} />
        <Route path="contratti" element={<ContrattiPage />} />
        <Route path="documenti" element={<DocumentiPage />} />
        <Route path="pagamenti" element={<PagamentiPage />} />
        <Route path="pagamenti-straordinari" element={<PagamentiStraordinariPage />} />
        <Route path="solleciti" element={<SollecitiPage />} />
        <Route path="avvisi" element={<AvvisiPage />} />
        <Route path="template-documenti" element={<TemplateDocumentiPage />} />
        <Route path="template-testi" element={<TemplateTestiPage />} />
        <Route path="audit-logs" element={<AuditLogsPage />} />
        <Route path="storage" element={<StoragePage />} />
        <Route path="email" element={<EmailPage />} />
        <Route path="registro-gdpr" element={<RegistroGdprPage />} />
        <Route path="centro-sicurezza" element={<SecurityCenterPage />} />
        <Route path="ghost/:userId" element={<GhostDashboard />} />
      </Route>

      {/* User routes */}
      <Route 
        path="/user" 
        element={
          <ProtectedRoute>
            <UserDashboard />
          </ProtectedRoute>
        } 
      />

      {/* Preview Portale Utenti (pubblico per demo) */}
      <Route path="/portale-preview" element={<PortaleUtentePreview />} />

      {/* Privacy Policy (pubblica) */}
      <Route path="/privacy" element={<PrivacyPage />} />

      {/* FAQ (pubblica) */}
      <Route path="/faq" element={<FaqPage />} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <CookieBanner />
        <Toaster richColors position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
